import React, {useEffect, useState} from 'react';
import {useAppHeaderAndPromptsVisibility} from '../contexts/AppHeaderAndPromptsVisibilityContext';
import {useAnalyticsPage} from '../hooks/useAnalyticsPage';
import {Box, Chip, IconButton, Typography} from '@mui/material';
import {
	entriesSetState,
	entriesHydratedSetState,
	locationResetState,
	locationIdSetState,
	selectAllEntries,
	selectLocationEntryLocal,
	selectIsEntriesHydrated,
	selectActiveRepositoryId,
} from './contentSlice';
import {useAppDispatch, useAppSelector} from '../hooks';
import useAuth, {useAuthEffects} from '../components/Auth';
import {localStorageGetIsIntroDialogCompleted, obtainLocalEntries, sessionStorageGetIsOfflineContinued, sessionStorageSetIsOfflineContinued, storeLocalEntries} from '../localStorage';
import HintsBanner from '../components/HintsBanner';
import {Link, useParams} from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import HeaderWrapper, { HeaderSlotContext } from '../components/HeaderWrapper';
import IntroDialog from '../components/IntroDialog';
import { isIntroDialogOpenSetState, selectIsIntroDialogOpen } from './authSlice';
import { selectSkipNextLocationReset, skipNextLocationResetSetState } from './navigationSlice';
import RepositoriesPanel from '../components/RepositoriesPanel';
import { EntityType, NoteEntry, TimelineEntry } from '../types';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import WelcomeBackDialog from '../components/WelcomeBackDialog';
import { getLandingPageLoadHadDemoSearchParam } from '../hooks/useOnboardingFlow';
import { getIsLandingLoginRedirectPending } from '../hooks/useLandingLoginRedirect';
import { useLoadEntryQuery } from '../services/records';
import { useGlassBarClass } from '../hooks/useGlassBarClass';
import { useSharedView } from '../hooks/useSharedView';
import { useBackNavigation } from '../hooks/useBackNavigation';

export function useContentWrapper() {
	const isContentLoaded = true;
	return {
		isContentLoaded,
	};
}

function ContentWrapper({repositoriesCollapse = false, children, noMarginTop = false, suppressIntroDialog: suppressIntroDialogProp = false, showBackBtnWithId = false, showBackBtn = false, backBtnTo, showSharedRepositoryBackBtn = false, showHintsBanner = false, analyticsPage}: {repositoriesCollapse?: boolean, children?: React.ReactNode, noMarginTop?: boolean, suppressIntroDialog?: boolean, showBackBtnWithId?: boolean, showBackBtn?: boolean, backBtnTo?: string, showSharedRepositoryBackBtn?: boolean, showHintsBanner?: boolean, /** Route name sent to GA4; GA4 sends nothing on routes without it. */ analyticsPage?: string}) {
	useAuthEffects();
	const glassBarClass = useGlassBarClass();
	const [headerSlotEl, setHeaderSlotEl] = useState<HTMLElement | null>(null);
	const { id } = useParams();
	const location = useLocation();
	const {isSharedView, toPath} = useSharedView();
	const suppressIntroDialog = suppressIntroDialogProp || isSharedView;
	const goBack = useBackNavigation();
	const activeRepositoryId = useAppSelector(selectActiveRepositoryId);
	const {isAuthenticating, isAuthenticated} = useAuth();
	const dispatch = useAppDispatch();
	const allEntries = useAppSelector(selectAllEntries);
	const isEntriesHydrated = useAppSelector(selectIsEntriesHydrated);
	const locationEntryLocal = useAppSelector(selectLocationEntryLocal);
	const skipNextLocationReset = useAppSelector(selectSkipNextLocationReset);
	const isIntroDialogOpen = useAppSelector(selectIsIntroDialogOpen);
	const { isSuccess: isRootEntryLoaded } = useLoadEntryQuery(null, {
		skip: isAuthenticating || !isAuthenticated,
	});
	const isWorkspaceReady = isAuthenticated ? isRootEntryLoaded : isEntriesHydrated;
	const isWorkspaceEmpty = allEntries.length === 0;
	const [wasIntroDialogCompletedOnPageLoad] = useState(() => localStorageGetIsIntroDialogCompleted());
	const [isWelcomeBackDialogOpen, setIsWelcomeBackDialogOpen] = useState(false);
	const [isWelcomeBackDialogEvaluated, setIsWelcomeBackDialogEvaluated] = useState(false);
	const {areAppHeaderAndPromptsHidden} = useAppHeaderAndPromptsVisibility();
	useAnalyticsPage(analyticsPage);
	
	// Run only when route id changes. skipNextLocationReset is read at run time; do not add to deps or the effect re-runs when we set it false and triggers locationResetState (flicker after slide).
	// TODO: phase out relying on central locationId control and use query params at each view instead
	useEffect(() => {
		const isRepositoryContextRoute =
			location.pathname.startsWith('/collection/') ||
			location.pathname.startsWith('/entries/') ||
			isSharedView;
		if (skipNextLocationReset) {
			dispatch(locationIdSetState(id ?? null));
			dispatch(skipNextLocationResetSetState(false));
		} else if (isRepositoryContextRoute) {
			// Preserve repository-scoped UI context while switching between repository sub-views.
			dispatch(locationIdSetState(id ?? null));
		} else {
			dispatch(locationResetState(id ?? null));
		}
	}, [id, location.pathname, isSharedView, dispatch]);

	/**
	 * Whether this mount has loaded the visitor's own entries from local storage. A shared view loads
	 * someone else's records into the same store, so nothing is saved until the local copy is reloaded.
	 */
	const [isLocalDataLoaded, setIsLocalDataLoaded] = useState(false);

	// while not authenticated, init data from local storage
	useEffect(() => {
		if (isAuthenticating || isAuthenticated || isSharedView) {
			setIsLocalDataLoaded(false);
			return;
		}
		const browserStorageEntries = obtainLocalEntries();
		dispatch(entriesSetState(browserStorageEntries));
		dispatch(entriesHydratedSetState(true));
		setIsLocalDataLoaded(true);
	}, [isAuthenticating, isAuthenticated, isSharedView, dispatch]);

	// while not authenticated, sync data to local storage on any change
	useEffect(() => {
		if (isAuthenticating || isAuthenticated || isSharedView || !isLocalDataLoaded) {
			return;
		}
		storeLocalEntries(allEntries);
	}, [isAuthenticating, isAuthenticated, isSharedView, isLocalDataLoaded, allEntries]);

	useEffect(() => {
		if (getIsLandingLoginRedirectPending() || isAuthenticating || suppressIntroDialog || !isWorkspaceReady) {
			return;
		}
		const isIntroDialogCompleted = localStorageGetIsIntroDialogCompleted();
		if (isIntroDialogCompleted) {
			return;
		}
		// An empty workspace gets the canvas welcome mode instead.
		const shouldShowIntro = !isAuthenticated && !isWorkspaceEmpty;
		if (shouldShowIntro) {
			dispatch(isIntroDialogOpenSetState(true));
		}
	}, [
		isAuthenticating,
		isAuthenticated,
		suppressIntroDialog,
		isWorkspaceReady,
		isWorkspaceEmpty,
		dispatch,
	]);

	useEffect(() => {
		if (getLandingPageLoadHadDemoSearchParam()) {
			setIsWelcomeBackDialogEvaluated(true);
			return;
		}
		if (getIsLandingLoginRedirectPending()) {
			return;
		}
		if (
			location.pathname !== '/' ||
			suppressIntroDialog ||
			isAuthenticating ||
			isAuthenticated ||
			!isEntriesHydrated ||
			isIntroDialogOpen ||
			!wasIntroDialogCompletedOnPageLoad ||
			sessionStorageGetIsOfflineContinued() ||
			isWelcomeBackDialogEvaluated
		) {
			return;
		}

		setIsWelcomeBackDialogEvaluated(true);
		setIsWelcomeBackDialogOpen(true);
	}, [
		location.pathname,
		suppressIntroDialog,
		isAuthenticating,
		isAuthenticated,
		isEntriesHydrated,
		isIntroDialogOpen,
		wasIntroDialogCompletedOnPageLoad,
		isWelcomeBackDialogEvaluated,
	]);

	const closeWelcomeBackDialog = () => {
		sessionStorageSetIsOfflineContinued(true);
		setIsWelcomeBackDialogOpen(false);
	};

	const sharedRepositoryBackBtnTo = isSharedView && showSharedRepositoryBackBtn && activeRepositoryId ? toPath(`/collection/${activeRepositoryId}`) : null;
	const backBtnRoute = sharedRepositoryBackBtnTo ?? backBtnTo ?? (locationEntryLocal?.parentId ? `/${locationEntryLocal?.parentId}` : '/');
	const shouldShowBackBtn = Boolean(sharedRepositoryBackBtnTo) || (!isSharedView && (showBackBtnWithId && id || showBackBtn));
	const contextTitle = locationEntryLocal?.type === EntityType.Timeline
		? (locationEntryLocal as TimelineEntry).name
		: (locationEntryLocal as NoteEntry)?.title ?? '';

	return (
		<Box display='flex' flexDirection='column' justifyContent='space-between' minHeight='calc(100dvh)'>
			{!areAppHeaderAndPromptsHidden && <HeaderWrapper slotRef={setHeaderSlotEl}>
				{(shouldShowBackBtn || (isSharedView && contextTitle)) && <Box display='flex' justifyContent='flex-start' alignItems='center' gap={1} className={glassBarClass} sx={{pr: contextTitle ? 2 : 0, pl: shouldShowBackBtn ? 0 : 2, minHeight: 48}}>
					{shouldShowBackBtn && <IconButton
						sx={{pointerEvents: 'all'}}
						component={Link}
						to={backBtnRoute}
						onClick={(e: React.MouseEvent) => {
							e.preventDefault();
							goBack(backBtnRoute);
						}}
						size='large'
					>
						<ArrowBackIcon />
					</IconButton>}
					{contextTitle && <Typography variant='h6' color='text.primary' sx={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>{contextTitle}</Typography>}
				</Box>}
				{isSharedView && isAuthenticated && <Chip
					component={Link}
					to='/'
					clickable
					icon={<HomeIcon fontSize='small' />}
					label='Return to my workspace'
					variant='outlined'
					sx={{px: 1, position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'all'}}
				/>}
			</HeaderWrapper>}
			{!isSharedView && !areAppHeaderAndPromptsHidden && <RepositoriesPanel forceCollapse={repositoriesCollapse} />}
			<Box flex={1} display='flex' flexDirection='column' mt={noMarginTop || areAppHeaderAndPromptsHidden ? 0 : 9}>
				<HeaderSlotContext.Provider value={headerSlotEl}>
					{children}
				</HeaderSlotContext.Provider>
			</Box>
			{showHintsBanner && !areAppHeaderAndPromptsHidden && <HintsBanner />}
			{!suppressIntroDialog && !areAppHeaderAndPromptsHidden && <IntroDialog />}
			{!suppressIntroDialog && !areAppHeaderAndPromptsHidden && (
				<WelcomeBackDialog
					open={isWelcomeBackDialogOpen && location.pathname === '/'}
					onClose={closeWelcomeBackDialog}
				/>
			)}
		</Box>
	);
}

export default ContentWrapper;
